/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaShortcutsDialog.tsx
 * FILE_ID: JOBHUB-QA-SHORTCUTS-DIALOG-001
 * PURPOSE: Remap QA shortcuts. Desktop only.
 */
import { useEffect, useState } from 'react'
import {
  QA_ACTIONS,
  effectiveQaCombo,
  loadQaShortcutOverrides,
  qaComboAllowed,
  qaComboFromEvent,
  qaFormatCombo,
  saveQaShortcutOverrides,
  type QaActionId,
  type QaShortcutOverrides,
} from '../../qa/qaShortcuts'

export function QaShortcutsDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [overrides, setOverrides] = useState<QaShortcutOverrides>({})
  const [capture, setCapture] = useState<QaActionId | null>(null)
  const [conflict, setConflict] = useState<string | null>(null)
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform)

  useEffect(() => {
    if (open) setOverrides(loadQaShortcutOverrides())
  }, [open])

  useEffect(() => {
    if (!capture) return
    const onKey = (event: KeyboardEvent) => {
      event.preventDefault()
      event.stopPropagation()
      if (event.key === 'Escape') {
        setCapture(null)
        setConflict(null)
        return
      }
      const combo = qaComboFromEvent(event)
      if (!combo || !qaComboAllowed(combo)) return
      const taken = QA_ACTIONS.find((action) => (
        action.id !== capture && effectiveQaCombo(overrides, action.id) === combo
      ))
      if (taken) {
        setConflict(`Already used by ${taken.label}`)
        return
      }
      const next = { ...overrides, [capture]: combo }
      setOverrides(next)
      saveQaShortcutOverrides(next)
      setConflict(null)
      setCapture(null)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [capture, overrides])

  if (!open) return null
  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Shortcuts"
        data-qa-shortcut-capture={capture ? '' : undefined}
        className="w-full max-w-md rounded-lg border border-border bg-card p-4 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">Shortcuts</p>
          <button type="button" className="text-xs text-muted-foreground" onClick={onClose}>Close</button>
        </div>
        <div className="max-h-[45vh] space-y-2 overflow-auto">
          {QA_ACTIONS.map((action) => (
            <div key={action.id} className="flex items-center justify-between gap-2">
              <span className="text-sm">{action.label}</span>
              <button
                type="button"
                className="rounded-md border border-border px-2 py-1 text-xs"
                onClick={() => { setCapture(action.id); setConflict(null) }}
              >
                {capture === action.id ? 'Press keys' : qaFormatCombo(effectiveQaCombo(overrides, action.id), isMac)}
              </button>
            </div>
          ))}
        </div>
        {conflict && <p className="mt-2 text-xs text-destructive">{conflict}</p>}
        <button
          type="button"
          className="mt-3 text-xs text-muted-foreground"
          onClick={() => {
            setOverrides({})
            saveQaShortcutOverrides({})
            setConflict(null)
          }}
        >
          Reset
        </button>
      </div>
    </div>
  )
}
